import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Award,
  GraduationCap,
  BookOpen,
  ShieldCheck,
  Building,
  User
} from 'lucide-react';

interface VerifiedCertificate {
  id: string;
  certificate_number: string;
  title: string;
  description: string;
  learning_outcomes: any;
  ects_credits: number;
  competency_points: number;
  eqf_level: number;
  assessment_result: {
    score: number;
    passed: boolean;
  };
  course_completion_date: string;
  exam_date: string;
  issue_date: string;
  valid_until: string | null;
  sensor_name: string;
  status: 'active' | 'revoked' | 'expired';
  verification_code: string;
  view_count: number;
  verification_count: number;
  last_verified_at: string | null;
  candidate_name: string;
  date_of_birth: string;
  member_name: string;
  association_name: string;
}

export function VerifyPage() {
  const { verificationCode } = useParams<{ verificationCode: string }>();
  const [certificate, setCertificate] = useState<VerifiedCertificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    verifyCode();
  }, [verificationCode]);

  async function verifyCode() {
    try {
      setLoading(true);
      const res = await fetch(`/api/certificates/verify/${verificationCode}`);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Attestkoden er ugyldig eller attesten er inaktiv');
        }
        throw new Error('Kunne ikke verifisere attest');
      }

      const data = await res.json();
      setCertificate(data.certificate);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-kf-orange/10 to-white flex items-center justify-center p-4">
        <div className="card max-w-2xl w-full text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-kf-orange mx-auto"></div>
          <p className="mt-6 text-lg text-kf-gray-600">Verifiserer attest...</p>
        </div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-kf-error-50 to-white flex items-center justify-center p-4">
        <div className="card max-w-2xl w-full text-center py-12">
          <XCircle className="w-20 h-20 text-kf-error-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-kf-gray-900 mb-3">Verifisering feilet</h1>
          <p className="text-lg text-kf-gray-600 mb-8">{error || 'Attest ikke funnet'}</p>
          <div className="p-4 bg-kf-orange-50 border border-kf-orange-200 rounded-lg">
            <p className="text-sm text-kf-orange-800">
              <strong>Merk:</strong> Attestkoder er unike og kan bare brukes til å verifisere ekte kompetanseattester.
              Hvis du mottok denne koden fra en person eller organisasjon, kontakt dem for å bekrefte korrekt kode.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isExpired = certificate.valid_until && new Date(certificate.valid_until) < new Date();
  const isRevoked = certificate.status === 'revoked';
  const isValid = certificate.status === 'active' && !isExpired && !isRevoked;

  return (
    <div className="min-h-screen bg-gradient-to-br from-kf-success-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Verification Status Banner */}
        <div className={`card ${isValid ? 'bg-kf-success-50 border-kf-success-200' : 'bg-kf-orange-50 border-kf-orange-200'}`}>
          <div className="text-center py-8">
            {isValid ? (
              <>
                <CheckCircle className="w-20 h-20 text-kf-success-600 mx-auto mb-4" />
                <h1 className="text-4xl font-bold text-kf-success-900 mb-2">✓ Attest Verifisert</h1>
                <p className="text-lg text-kf-success-700">
                  Denne kompetanseattesten er autentisk og utstedt av en godkjent organisasjon
                </p>
              </>
            ) : (
              <>
                <AlertCircle className="w-20 h-20 text-kf-orange-600 mx-auto mb-4" />
                <h1 className="text-4xl font-bold text-kf-orange-900 mb-2">⚠ Attest funnet med advarsel</h1>
                <p className="text-lg text-kf-orange-700">
                  {isExpired && 'Denne attesten har utløpt'}
                  {isRevoked && 'Denne attesten er tilbakekalt'}
                </p>
              </>
            )}
          </div>

          {/* Status Badges */}
          <div className="flex justify-center gap-3 mt-4">
            {certificate.status === 'active' && !isExpired && (
              <span className="px-4 py-2 bg-kf-success-100 text-kf-success-800 text-sm font-semibold rounded-full flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Aktiv
              </span>
            )}
            {isExpired && (
              <span className="px-4 py-2 bg-kf-orange-100 text-kf-orange-800 text-sm font-semibold rounded-full">
                Utløpt
              </span>
            )}
            {isRevoked && (
              <span className="px-4 py-2 bg-kf-error-100 text-kf-error-800 text-sm font-semibold rounded-full">
                Tilbakekalt
              </span>
            )}
          </div>
        </div>

        {/* Certificate Holder Information */}
        <div className="card">
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-6 flex items-center gap-2">
            <User className="w-6 h-6" />
            Attestinnehaver
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-semibold text-kf-gray-700 mb-1">Navn</p>
              <p className="text-lg text-kf-gray-900">{certificate.candidate_name}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-kf-gray-700 mb-1">Fødselsdato</p>
              <p className="text-lg text-kf-gray-900">
                {new Date(certificate.date_of_birth).toLocaleDateString('no-NO', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Certificate Details */}
        <div className="card">
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-6 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" />
            Attestdetaljer
          </h2>

          <div className="space-y-6">
            {/* Title */}
            <div>
              <p className="text-sm font-semibold text-kf-gray-700 mb-1">Tittel</p>
              <p className="text-xl font-bold text-kf-gray-900">{certificate.title}</p>
            </div>

            {/* Certificate Number */}
            <div>
              <p className="text-sm font-semibold text-kf-gray-700 mb-1">Attestnummer</p>
              <p className="text-lg font-mono text-kf-gray-900">{certificate.certificate_number}</p>
            </div>

            {/* Description */}
            {certificate.description && (
              <div>
                <p className="text-sm font-semibold text-kf-gray-700 mb-1">Beskrivelse</p>
                <p className="text-kf-gray-700 whitespace-pre-wrap">{certificate.description}</p>
              </div>
            )}

            {/* Grid of Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
              <div>
                <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  ECTS-poeng
                </p>
                <p className="text-kf-gray-900">{certificate.ects_credits} ECTS</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" />
                  EQF-nivå
                </p>
                <p className="text-kf-gray-900">Nivå {certificate.eqf_level}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Utstedelsesdato
                </p>
                <p className="text-kf-gray-900">
                  {new Date(certificate.issue_date).toLocaleDateString('no-NO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Gyldig til
                </p>
                <p className="text-kf-gray-900">
                  {certificate.valid_until
                    ? new Date(certificate.valid_until).toLocaleDateString('no-NO', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : 'Ingen utløpsdato'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Issuing Organization */}
        <div className="card">
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-6 flex items-center gap-2">
            <Building className="w-6 h-6" />
            Utsteder
          </h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-kf-gray-700 mb-1">Organisasjon</p>
              <p className="text-lg text-kf-gray-900">{certificate.member_name}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-kf-gray-700 mb-1">Studieforbund</p>
              <p className="text-kf-gray-900">{certificate.association_name}</p>
            </div>
            {certificate.sensor_name && certificate.sensor_name !== 'Sensor' && (
              <div>
                <p className="text-sm font-semibold text-kf-gray-700 mb-1">Sensor</p>
                <p className="text-kf-gray-900">{certificate.sensor_name}</p>
              </div>
            )}
          </div>
        </div>

        {/* Note: Assessment results (score, grade) are intentionally NOT shown on public verification */}
        {/* This information is only available via the candidate's private share link */}

        {/* Learning Outcomes */}
        {certificate.learning_outcomes && (
          <div className="card">
            <h2 className="text-2xl font-bold text-kf-gray-900 mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              Læringsutbytter
            </h2>
            <div className="space-y-6">
              {certificate.learning_outcomes.knowledge && certificate.learning_outcomes.knowledge.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-kf-gray-900 mb-3">Kunnskap</h3>
                  <ul className="list-disc list-inside space-y-2 text-kf-gray-700">
                    {certificate.learning_outcomes.knowledge.map((item: string, idx: number) => (
                      <li key={idx} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {certificate.learning_outcomes.skills && certificate.learning_outcomes.skills.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-kf-gray-900 mb-3">Ferdigheter</h3>
                  <ul className="list-disc list-inside space-y-2 text-kf-gray-700">
                    {certificate.learning_outcomes.skills.map((item: string, idx: number) => (
                      <li key={idx} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {certificate.learning_outcomes.competence && certificate.learning_outcomes.competence.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-kf-gray-900 mb-3">Generell kompetanse</h3>
                  <ul className="list-disc list-inside space-y-2 text-kf-gray-700">
                    {certificate.learning_outcomes.competence.map((item: string, idx: number) => (
                      <li key={idx} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Verification Stats */}
        <div className="card bg-kf-gray-50">
          <div className="text-center">
            <p className="text-sm text-kf-gray-600 mb-2">Denne attesten har blitt verifisert</p>
            <p className="text-4xl font-bold text-kf-orange-600">{certificate.verification_count}</p>
            <p className="text-sm text-kf-gray-600 mt-1">
              {certificate.verification_count === 1 ? 'gang' : 'ganger'}
            </p>
            {certificate.last_verified_at && (
              <p className="text-xs text-kf-gray-500 mt-2">
                Sist verifisert:{' '}
                {new Date(certificate.last_verified_at).toLocaleDateString('no-NO', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
        </div>

        {/* Footer Notice */}
        <div className="card bg-kf-blue-50 border-kf-blue-200">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-kf-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-kf-blue-900 mb-2">Om verifisering</h3>
              <p className="text-sm text-kf-blue-800">
                Denne attesten er digitalt signert og verifisert av Kompetanseforbundet.
                Verifikasjonskoden er unik for denne attesten og kan ikke kopieres eller forfalskes.
                Hvis du har spørsmål om attestens gyldighet, kontakt utstedende organisasjon direkte.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
